/**
 * v-virtual-scroll 평면 리스트 — 펼쳐진 섹션 헤더 sticky (복제 고정) 판정
 *
 * ## 패턴 (다른 리스트에 적용할 때)
 * 1. 트리를 flatRows + 고정 rowHeight 로 평탄화
 * 2. sticky 대상 레벨마다 PinLevel 정의 (isHeader / isExpanded / isSectionEnd)
 * 3. resolvePinnedVirtualScrollHierarchy(levels, { flatRows, scrollTop, rowHeight })
 * 4. UI: virtual-scroll **위에** pinned 행을 레벨 순으로 렌더, 리스트 안 원본은 shouldHidePinnedVirtualRow 로 숨김
 * 5. scrollTop 은 virtual-scroll 의 $el 에 passive listener (+ @scroll.native 보조)
 *
 * @typedef {object} VirtualScrollPinLevel
 * @property {string} id — 'array' | 'string' 등 레벨 식별자
 * @property {(row: object) => boolean} isHeader
 * @property {(row: object) => boolean} isExpanded
 * @property {(row: object) => boolean} isSectionEnd — 이 행이 나오면 현재 섹션 종료
 */

/**
 * @param {VirtualScrollPinLevel} level
 * @param {{ flatRows: object[], scrollTop: number, rowHeight: number }} params
 * @returns {object|null}
 */
export function resolvePinnedVirtualScrollHeader(level, { flatRows, scrollTop, rowHeight }) {
  const { isHeader, isExpanded, isSectionEnd } = level;

  if (!Array.isArray(flatRows) || !flatRows.length || rowHeight <= 0) {
    return null;
  }

  const safeScrollTop = Math.max(0, scrollTop);
  const firstVisibleIndex = Math.min(
    flatRows.length - 1,
    Math.floor(safeScrollTop / rowHeight)
  );

  let headerIndex = -1;
  let headerRow = null;

  for (let i = 0; i <= firstVisibleIndex; i++) {
    const row = flatRows[i];
    if (isHeader(row) && isExpanded(row)) {
      headerIndex = i;
      headerRow = row;
    }
  }

  if (!headerRow || headerIndex < 0) {
    return null;
  }

  let sectionEnd = flatRows.length;
  for (let i = headerIndex + 1; i < flatRows.length; i++) {
    if (isSectionEnd(flatRows[i])) {
      sectionEnd = i;
      break;
    }
  }

  if (firstVisibleIndex < headerIndex || firstVisibleIndex >= sectionEnd) {
    return null;
  }

  const headerTop = headerIndex * rowHeight;
  if (safeScrollTop <= headerTop) {
    return null;
  }

  return headerRow;
}

/**
 * @param {VirtualScrollPinLevel[]} levels — 상단 고정 순서 (바깥 → 안쪽)
 * @returns {{ id: string, row: object }[]} 고정할 행만 id 순서대로
 */
export function resolvePinnedVirtualScrollHierarchy(levels, params) {
  if (!Array.isArray(levels) || !levels.length) {
    return [];
  }
  return levels
    .map((level) => ({
      id: level.id,
      row: resolvePinnedVirtualScrollHeader(level, params)
    }))
    .filter((entry) => entry.row != null);
}

/**
 * 리스트 안에서 이미 상단에 복제 고정된 헤더 행 숨김
 * @param {object} row
 * @param {object[]} pinnedEntries — resolvePinnedVirtualScrollHierarchy 결과
 * @param {(a: object, b: object) => boolean} [isSameRow]
 */
export function shouldHidePinnedVirtualRow(
  row,
  pinnedEntries,
  isSameRow = (a, b) => a === b
) {
  if (!row || !pinnedEntries?.length) {
    return false;
  }
  return pinnedEntries.some((entry) => entry.row && isSameRow(row, entry.row));
}

/** 결함 드로어 모듈 리스트 (array_header / string_header) */
export const DEFECT_MODULE_LIST_PIN_LEVELS = [
  {
    id: 'array',
    isHeader: (row) => row?.kind === 'array_header',
    isExpanded: (row) => row?.expanded === true,
    isSectionEnd: (row) => row?.kind === 'array_header'
  },
  {
    id: 'string',
    isHeader: (row) => row?.kind === 'string_header',
    isExpanded: (row) => row?.expanded === true,
    isSectionEnd: (row) => row?.kind === 'string_header' || row?.kind === 'array_header'
  }
];

/** 패널 플래닝 모듈 트리 (item.type: array / string) */
export const MODULE_TREE_PIN_LEVELS = [
  {
    id: 'array',
    isHeader: (row) => row?.item?.type === 'array',
    isExpanded: (row) => row?.expanded === true,
    isSectionEnd: (row) => row?.item?.type === 'array'
  },
  {
    id: 'string',
    isHeader: (row) => row?.item?.type === 'string',
    isExpanded: (row) => row?.expanded === true,
    isSectionEnd: (row) => {
      const type = row?.item?.type;
      return type === 'string' || type === 'array';
    }
  }
];

/**
 * id 맵 형태로 반환 — 기존 arrayRow / stringRow API 호환
 * @param {VirtualScrollPinLevel[]} levels
 * @returns {Record<string, object|null>}
 */
export function resolvePinnedVirtualScrollHierarchyMap(levels, params) {
  const map = {};
  levels.forEach((level) => {
    map[level.id] = null;
  });
  resolvePinnedVirtualScrollHierarchy(levels, params).forEach(({ id, row }) => {
    map[id] = row;
  });
  return map;
}

/**
 * 가상 스크롤에서 행이 sticky 핀(Array/String) 아래에 보이게 할 scrollTop.
 * 행을 컨테이너 맨 위에 두면 핀에 가려진다.
 * @param {number} rowIndex
 * @param {number} rowHeight
 * @param {number} pinCount
 * @returns {number}
 */
export function virtualScrollTopToRevealRow(rowIndex, rowHeight, pinCount) {
  const idx = Number(rowIndex);
  const h = Number(rowHeight);
  const pins = Math.max(0, Math.floor(Number(pinCount) || 0));
  if (!Number.isFinite(idx) || idx < 0 || !Number.isFinite(h) || h <= 0) {
    return 0;
  }
  return Math.max(0, idx * h - pins * h);
}
